

import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, limit, startAfter, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust the import to match your Firebase configuration
import '../style/Designs.css';

const Designs = () => {
  const [designs, setDesigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState('all');

  // Size options for SKU generation
  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchDesigns();
  }, [selectedUser, selectedProduct]);

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchDesigns = async () => {
    setLoading(true);
    try {
      const allDesigns = [];
      const productSet = new Set();
      
      if (selectedUser === 'all') {
        // Fetch designs from all users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        
        for (const userDoc of usersSnapshot.docs) {
          const userId = userDoc.id;
          const userData = userDoc.data();
          
          const designsQuery = query(
            collection(db, 'users', userId, 'designs'),
            orderBy('designName')
          );

          const designsSnapshot = await getDocs(designsQuery);
          
          designsSnapshot.forEach(doc => {
            const designData = doc.data();
            
            // Add product name to set for filter options
            if (designData.productName) {
              productSet.add(designData.productName);
            }
            
            // Filter by product if selected
            if (selectedProduct === 'all' || designData.productName === selectedProduct) {
              allDesigns.push({
                designId: doc.id,
                ...designData,
                userId,
                userName: userData.brandName || userData.name || userData.email || userId,
              });
            }
          });
        }
      } else {
        // Fetch designs from selected user only
        const userData = users.find(user => user.id === selectedUser);
        
        const designsQuery = query(
          collection(db, 'users', selectedUser, 'designs'),
          orderBy('designName')
        );

        const designsSnapshot = await getDocs(designsQuery);
        
        designsSnapshot.forEach(doc => {
          const designData = doc.data();
          
          // Add product name to set for filter options
          if (designData.productName) {
            productSet.add(designData.productName);
          }
          
          // Filter by product if selected
          if (selectedProduct === 'all' || designData.productName === selectedProduct) {
            allDesigns.push({
              designId: doc.id,
              ...designData,
              userId: selectedUser,
              userName: userData?.brandName || userData?.name || userData?.email || selectedUser,
            });
          }
        });
      }

      // Update products list for filter dropdown
      setProducts(Array.from(productSet).sort());
      setDesigns(allDesigns);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching designs:", error);
      setLoading(false);
    }
  };

  const getBaseSKU = (existingSKUs = []) => {
    if (existingSKUs.length > 0) {
      // Extract base from first existing SKU (e.g., "u-50053-S" -> "u-50053")
      const parts = existingSKUs[0].split('-');
      return parts.slice(0, -1).join('-'); // Remove last part (size) and rejoin
    }
    return null;
  };

  const generateSKU = (existingSKUs, designId, size) => {
    const baseSKU = getBaseSKU(existingSKUs);
    if (baseSKU) {
      return `${baseSKU}-${size}`;
    }
    // Fallback to original format if no existing SKUs
    return `u-${designId}-${size}`;
  };

  const getAvailableSizes = (existingSKUs = []) => {
    // Extract sizes from existing SKUs
    const existingSizes = existingSKUs.map(sku => {
      const parts = sku.split('-');
      return parts[parts.length - 1]; // Get the last part (size)
    });
    
    // Return sizes that are not already in the SKUs array
    return sizeOptions.filter(size => !existingSizes.includes(size));
  };

  const handleAddSize = async (designIndex, selectedSize) => {
    const design = designs[designIndex];
    const newSKU = generateSKU(design.SKUs, design.designId, selectedSize);
    
    try {
      // Add the new SKU to the existing SKUs array in Firestore
      const designRef = doc(db, 'users', design.userId, 'designs', design.designId);
      await updateDoc(designRef, {
        SKUs: arrayUnion(newSKU)
      });

      // Update local state
      const updatedDesigns = [...designs];
      const currentSKUs = updatedDesigns[designIndex].SKUs || [];
      updatedDesigns[designIndex] = {
        ...updatedDesigns[designIndex],
        SKUs: [...currentSKUs, newSKU]
      };
      setDesigns(updatedDesigns);
      
      console.log(`New SKU added: ${newSKU}`);
    } catch (error) {
      console.error("Error adding SKU:", error);
    }
  };

  const handleUserFilter = (userId) => {
    setSelectedUser(userId);
  };

  const handleProductFilter = (productName) => {
    setSelectedProduct(productName);
  };



  if (loading) {
    return <p>Loading designs...</p>;
  }

  return (
    <div className="designs-page">
      <h1>Designs</h1>
      
      {/* Filters */}
      <div className="filters" style={{ marginBottom: '20px', display: 'flex', gap: '20px', alignItems: 'center' }}>
        {/* User Filter */}
        <div className="user-filter">
          <label htmlFor="user-select">Filter by User: </label>
          <select 
            id="user-select"
            value={selectedUser} 
            onChange={(e) => handleUserFilter(e.target.value)}
            style={{ padding: '8px', marginLeft: '10px' }}
          >
            <option value="all">All Users</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.brandName || user.email || user.id}
              </option>
            ))}
          </select>
        </div>

        {/* Product Filter */}
        <div className="product-filter">
          <label htmlFor="product-select">Filter by Product: </label>
          <select 
            id="product-select"
            value={selectedProduct} 
            onChange={(e) => handleProductFilter(e.target.value)}
            style={{ padding: '8px', marginLeft: '10px' }}
          >
            <option value="all">All Products</option>
            {products.map(product => (
              <option key={product} value={product}>
                {product}
              </option>
            ))}
          </select>
        </div>
      </div>

      <table className="designs-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Design Name</th>
            <th>Existing SKUs</th>
            <th>Add New Size</th>
            <th>Front Design</th>
            <th>Back Design</th>
            <th>Print Cost</th>
            <th>Base Cost</th>
            <th>Total Price</th>
            <th>Product Name</th>
            <th>Product GSM</th>
          </tr>
        </thead>
        <tbody>
          {designs.map((design, index) => {
            const availableSizes = getAvailableSizes(design.SKUs);
            
            return (
              <tr key={`${design.userId}-${design.designId}`}>
                <td>{design.userName}</td>
                <td>{design.designName || 'N/A'}</td>
                <td>
                  <div style={{ maxWidth: '200px' }}>
                    {design.SKUs && design.SKUs.length > 0 ? (
                      <div>
                        {design.SKUs.map((sku, skuIndex) => (
                          <span 
                            key={skuIndex}
                            style={{ 
                              display: 'inline-block',
                              backgroundColor: '#f0f0f0',
                              padding: '2px 6px',
                              margin: '2px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            {sku}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'No SKUs'
                    )}
                  </div>
                </td>
                <td>
                  {availableSizes.length > 0 ? (
                    <select 
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddSize(index, e.target.value);
                          e.target.value = ''; // Reset selection
                        }
                      }}
                      style={{ padding: '4px' }}
                    >
                      <option value="">Add Size</option>
                      {availableSizes.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ color: '#888', fontSize: '12px' }}>All sizes added</span>
                  )}
                </td>
                <td>
                  {design.frontDesign ? (
                    <img 
                      src={design.frontDesign} 
                      alt="Front Design" 
                      style={{ width: '60px', height: 'auto' }} 
                    />
                  ) : 'N/A'}
                </td>
                <td>
                  {design.backDesign ? (
                    <img 
                      src={design.backDesign} 
                      alt="Back Design" 
                      style={{ width: '60px', height: 'auto' }} 
                    />
                  ) : 'N/A'}
                </td>
                <td>{design.PrintCost || 'N/A'}</td>
                <td>{design.baseCost || 'N/A'}</td>
                <td>{design.totalPrice || 'N/A'}</td>
                <td>{design.productName || 'N/A'}</td>
                <td>{design.productGSM || 'N/A'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {designs.length === 0 && !loading && (
        <p style={{ textAlign: 'center', margin: '20px' }}>
          No designs found {selectedUser !== 'all' || selectedProduct !== 'all' ? 'for selected filters' : ''}.
        </p>
      )}
    </div>
  );
};

export default Designs;


// import React, { useEffect, useState } from 'react';
// import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
// import { db } from '../firebase'; // Adjust the import to match your Firebase configuration
// import * as XLSX from 'xlsx'; // You'll need to install: npm install xlsx
// import '../style/Designs.css';

// const BrandDesignDetails = () => {
//   const [designs, setDesigns] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [brandInfo, setBrandInfo] = useState(null);
//   const [updating, setUpdating] = useState({});
  
//   // The specific brand ID you want to fetch
//   const BRAND_ID = 'jM2qV9IKOdS6rqMqntrsK1IAhJA3';

//   // Cost structure for different print methods
//   const costStructure = {
//     'DTG Epson': {
//       logo: 60,
//       LessthenA4: 90,
//       A4less: 138,
//       A4: 114,
//       A3: 162,
//       full: 198,
//     },
//     'DTF Epson': {
//       logo: 36,
//       LessthenA4: 66,
//       A4less: 102,
//       A4: 90,
//       A3: 120,
//       full: 150,
//     },
//   };

//   useEffect(() => {
//     fetchBrandDesigns();
//   }, []);

//   const fetchBrandDesigns = async () => {
//     setLoading(true);
//     try {
//       // Fetch brand/user information
//       const userDoc = await getDocs(query(collection(db, 'users')));
//       const brandData = userDoc.docs.find(doc => doc.id === BRAND_ID)?.data();
//       setBrandInfo(brandData);

//       // Fetch designs from the specific brand
//       const designsQuery = query(
//         collection(db, 'users', BRAND_ID, 'designs'),
//         orderBy('designName')
//       );

//       const designsSnapshot = await getDocs(designsQuery);
//       const designsList = designsSnapshot.docs.map(doc => ({
//         designId: doc.id,
//         ...doc.data(),
//         userId: BRAND_ID,
//         userName: brandData?.brandName || brandData?.name || brandData?.email || BRAND_ID,
//       }));

//       setDesigns(designsList);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching brand designs:", error);
//       setLoading(false);
//     }
//   };

//   // Function to determine cost category based on current cost
//   const getCostCategory = (currentCost, currentPrintMethod) => {
//     if (!currentCost || !currentPrintMethod) return 'logo';
    
//     const currentCostStructure = costStructure[currentPrintMethod];
//     if (!currentCostStructure) return 'logo';

//     // Find the closest matching cost category
//     const categories = Object.keys(currentCostStructure);
//     let closestCategory = 'logo';
//     let minDifference = Math.abs(currentCost - currentCostStructure['logo']);

//     categories.forEach(category => {
//       const difference = Math.abs(currentCost - currentCostStructure[category]);
//       if (difference < minDifference) {
//         minDifference = difference;
//         closestCategory = category;
//       }
//     });

//     return closestCategory;
//   };

//   // Function to calculate new costs based on print method
//   const calculateNewCosts = (design, newPrintMethod) => {
//     const newCostStructure = costStructure[newPrintMethod];
//     const currentPrintMethod = design.printMethod;
    
//     if (!newCostStructure || !currentPrintMethod) return design;

//     let newFrontCost = 0;
//     let newBackCost = 0;

//     // Calculate front cost based on current front cost
//     if (design.costs?.front) {
//       const frontCategory = getCostCategory(design.costs.front, currentPrintMethod);
//       newFrontCost = newCostStructure[frontCategory] || 0;
//     }

//     // Calculate back cost based on current back cost
//     if (design.costs?.back) {
//       const backCategory = getCostCategory(design.costs.back, currentPrintMethod);
//       newBackCost = newCostStructure[backCategory] || 0;
//     }

//     const newPrintCost = newFrontCost + newBackCost;
//     const baseCost = design.baseCost || 0;
//     const newTotalPrice = baseCost + newPrintCost;

//     return {
//       ...design,
//       printMethod: newPrintMethod,
//       PrintCost: newPrintCost,
//       totalPrice: newTotalPrice,
//       costs: {
//         front: newFrontCost,
//         back: newBackCost,
//       },
//     };
//   };

//   // Function to update design in Firebase
//   const updateDesignInFirebase = async (designId, updatedData) => {
//     setUpdating(prev => ({ ...prev, [designId]: true }));
    
//     try {
//       const designRef = doc(db, 'users', BRAND_ID, 'designs', designId);
//       await updateDoc(designRef, {
//         printMethod: updatedData.printMethod,
//         PrintCost: updatedData.PrintCost,
//         totalPrice: updatedData.totalPrice,
//         costs: updatedData.costs,
//       });

//       // Update local state
//       setDesigns(prevDesigns =>
//         prevDesigns.map(design =>
//           design.designId === designId ? updatedData : design
//         )
//       );

//       alert('Design updated successfully!');
//     } catch (error) {
//       console.error('Error updating design:', error);
//       alert('Error updating design. Please try again.');
//     } finally {
//       setUpdating(prev => ({ ...prev, [designId]: false }));
//     }
//   };

//   // Function to handle print method change
//   const handlePrintMethodChange = (design, newPrintMethod) => {
//     if (newPrintMethod === design.printMethod) return;

//     const updatedDesign = calculateNewCosts(design, newPrintMethod);
//     updateDesignInFirebase(design.designId, updatedDesign);
//   };

//   // Function to update all designs to a specific print method
//   const updateAllDesigns = async (newPrintMethod) => {
//     if (!window.confirm(`Are you sure you want to update all designs to ${newPrintMethod}?`)) {
//       return;
//     }

//     setLoading(true);
    
//     try {
//       const updatePromises = designs.map(async (design) => {
//         if (design.printMethod === newPrintMethod) return;

//         const updatedDesign = calculateNewCosts(design, newPrintMethod);
//         const designRef = doc(db, 'users', BRAND_ID, 'designs', design.designId);
        
//         return updateDoc(designRef, {
//           printMethod: updatedDesign.printMethod,
//           PrintCost: updatedDesign.PrintCost,
//           totalPrice: updatedDesign.totalPrice,
//           costs: updatedDesign.costs,
//         });
//       });

//       await Promise.all(updatePromises);
      
//       // Refresh the data
//       await fetchBrandDesigns();
//       alert('All designs updated successfully!');
//     } catch (error) {
//       console.error('Error updating all designs:', error);
//       alert('Error updating designs. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatDataForExcel = (designs) => {
//     return designs.map(design => ({
//       'Design ID': design.designId,
//       'Brand Name': design.userName,
//       'Design Name': design.designName || 'N/A',
//       'Description': design.description || 'N/A',
//       'Product Name': design.productName || 'N/A',
//       'Product GSM': design.productGSM || 'N/A',
//       'Print Method': design.printMethod || 'N/A',
//       'Print Cost': design.PrintCost || 'N/A',
//       'Base Cost': design.baseCost || 'N/A',
//       'Total Price': design.totalPrice || 'N/A',
//       'Selling Price': design.sellingPrice || 'N/A',
//       'SKUs': design.SKUs ? design.SKUs.join(', ') : 'No SKUs',
//       'Front Design URL': design.frontDesign || 'N/A',
//       'Back Design URL': design.backDesign || 'N/A',
//       'Front Mockup URL': design.images?.front || 'N/A',
//       'Back Mockup URL': design.images?.back || 'N/A',
//       'Front Print Width': design.Printsizes?.front?.width || 'N/A',
//       'Front Print Height': design.Printsizes?.front?.height || 'N/A',
//       'Back Print Width': design.Printsizes?.back?.width || 'N/A',
//       'Back Print Height': design.Printsizes?.back?.height || 'N/A',
//       'Front Cost': design.costs?.front || 'N/A',
//       'Back Cost': design.costs?.back || 'N/A',
//       'Timestamp': design.timestamp ? new Date(design.timestamp.seconds * 1000).toLocaleString() : 'N/A'
//     }));
//   };

//   const downloadExcel = () => {
//     if (designs.length === 0) {
//       alert('No designs to export');
//       return;
//     }

//     const formattedData = formatDataForExcel(designs);
//     const worksheet = XLSX.utils.json_to_sheet(formattedData);
    
//     // Auto-size columns
//     const colWidths = [];
//     Object.keys(formattedData[0]).forEach(key => {
//       const maxWidth = Math.max(
//         key.length,
//         ...formattedData.map(row => String(row[key] || '').length)
//       );
//       colWidths.push({ width: Math.min(maxWidth + 2, 50) });
//     });
//     worksheet['!cols'] = colWidths;

//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, 'Brand Designs');
    
//     const brandName = brandInfo?.brandName || brandInfo?.name || 'Brand';
//     const fileName = `${brandName}_Designs_${new Date().toISOString().split('T')[0]}.xlsx`;
    
//     XLSX.writeFile(workbook, fileName);
//   };

//   const downloadIndividualDesign = (design) => {
//     const formattedData = formatDataForExcel([design]);
//     const worksheet = XLSX.utils.json_to_sheet(formattedData);
    
//     // Auto-size columns
//     const colWidths = [];
//     Object.keys(formattedData[0]).forEach(key => {
//       const maxWidth = Math.max(
//         key.length,
//         String(formattedData[0][key] || '').length
//       );
//       colWidths.push({ width: Math.min(maxWidth + 2, 50) });
//     });
//     worksheet['!cols'] = colWidths;

//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, design.designName || 'Design');
    
//     const fileName = `${design.designName || design.designId}_Details.xlsx`;
//     XLSX.writeFile(workbook, fileName);
//   };

//   if (loading) {
//     return <p>Loading brand designs...</p>;
//   }

//   return (
//     <div className="designs-page">
//       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
//         <h1>Brand Design Details</h1>
//         <div style={{ display: 'flex', gap: '10px' }}>
//           <button 
//             onClick={() => updateAllDesigns('DTF Epson')}
//             style={{
//               backgroundColor: '#FF6B6B',
//               color: 'white',
//               padding: '10px 20px',
//               border: 'none',
//               borderRadius: '5px',
//               cursor: 'pointer',
//               fontSize: '14px'
//             }}
//           >
//             Update All to DTF Epson
//           </button>
//           <button 
//             onClick={() => updateAllDesigns('DTG Epson')}
//             style={{
//               backgroundColor: '#4ECDC4',
//               color: 'white',
//               padding: '10px 20px',
//               border: 'none',
//               borderRadius: '5px',
//               cursor: 'pointer',
//               fontSize: '14px'
//             }}
//           >
//             Update All to DTG Epson
//           </button>
//           <button 
//             onClick={downloadExcel}
//             style={{
//               backgroundColor: '#4CAF50',
//               color: 'white',
//               padding: '10px 20px',
//               border: 'none',
//               borderRadius: '5px',
//               cursor: 'pointer',
//               fontSize: '16px'
//             }}
//             disabled={designs.length === 0}
//           >
//             Download All Designs (Excel)
//           </button>
//         </div>
//       </div>
      
//       {brandInfo && (
//         <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
//           <h3>Brand Information</h3>
//           <p><strong>Brand Name:</strong> {brandInfo.brandName || brandInfo.name || 'N/A'}</p>
//           <p><strong>Email:</strong> {brandInfo.email || 'N/A'}</p>
//           <p><strong>Brand ID:</strong> {BRAND_ID}</p>
//           <p><strong>Total Designs:</strong> {designs.length}</p>
//         </div>
//       )}

//       <div className="designs-grid" style={{ display: 'grid', gap: '20px' }}>
//         {designs.map((design, index) => (
//           <div 
//             key={`${design.userId}-${design.designId}`} 
//             style={{
//               border: '1px solid #ddd',
//               borderRadius: '8px',
//               padding: '20px',
//               backgroundColor: '#fff',
//               boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
//             }}
//           >
//             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
//               <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>{design.designName || 'Unnamed Design'}</h3>
//               <button 
//                 onClick={() => downloadIndividualDesign(design)}
//                 style={{
//                   backgroundColor: '#008CBA',
//                   color: 'white',
//                   padding: '5px 10px',
//                   border: 'none',
//                   borderRadius: '3px',
//                   cursor: 'pointer',
//                   fontSize: '12px'
//                 }}
//               >
//                 Download Excel
//               </button>
//             </div>

//             {/* Print Method Selector - Top Level */}
//             <div style={{ 
//               marginBottom: '20px', 
//               padding: '15px', 
//               backgroundColor: '#e3f2fd', 
//               borderRadius: '8px',
//               border: '2px solid #2196f3'
//             }}>
//               <div style={{ marginBottom: '8px' }}>
//                 <strong style={{ color: '#1976d2', fontSize: '16px' }}>🖨️ Print Method:</strong>
//               </div>
//               <select
//                 value={design.printMethod || 'DTF Epson'}
//                 onChange={(e) => handlePrintMethodChange(design, e.target.value)}
//                 disabled={updating[design.designId]}
//                 style={{
//                   width: '100%',
//                   padding: '12px 16px',
//                   borderRadius: '6px',
//                   border: '2px solid #2196f3',
//                   backgroundColor: updating[design.designId] ? '#f5f5f5' : 'white',
//                   cursor: updating[design.designId] ? 'not-allowed' : 'pointer',
//                   fontSize: '16px',
//                   fontWeight: '600',
//                   color: '#1976d2'
//                 }}
//               >
//                 <option value="DTF Epson">DTF Epson</option>
//                 <option value="DTG Epson">DTG Epson</option>
//               </select>
//               {updating[design.designId] && (
//                 <div style={{ marginTop: '8px', color: '#4caf50', fontSize: '14px', fontWeight: '600' }}>
//                   ⏳ Updating costs and saving to database...
//                 </div>
//               )}
//             </div>

//             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
//               {/* Design Images */}
//               <div>
//                 <h4>Design Images</h4>
//                 <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
//                   {design.frontDesign && (
//                     <div>
//                       <p style={{ fontSize: '12px', margin: '0 0 5px 0' }}>Front Design</p>
//                       <img 
//                         src={design.frontDesign} 
//                         alt="Front Design" 
//                         style={{ width: '80px', height: 'auto', border: '1px solid #ddd' }} 
//                       />
//                     </div>
//                   )}
//                   {design.backDesign && (
//                     <div>
//                       <p style={{ fontSize: '12px', margin: '0 0 5px 0' }}>Back Design</p>
//                       <img 
//                         src={design.backDesign} 
//                         alt="Back Design" 
//                         style={{ width: '80px', height: 'auto', border: '1px solid #ddd' }} 
//                       />
//                     </div>
//                   )}
//                 </div>

//                 {/* Mockup Images */}
//                 {(design.images?.front || design.images?.back) && (
//                   <div>
//                     <h5>Mockups</h5>
//                     <div style={{ display: 'flex', gap: '10px' }}>
//                       {design.images?.front && (
//                         <div>
//                           <p style={{ fontSize: '12px', margin: '0 0 5px 0' }}>Front Mockup</p>
//                           <img 
//                             src={design.images.front} 
//                             alt="Front Mockup" 
//                             style={{ width: '80px', height: 'auto', border: '1px solid #ddd' }} 
//                           />
//                         </div>
//                       )}
//                       {design.images?.back && (
//                         <div>
//                           <p style={{ fontSize: '12px', margin: '0 0 5px 0' }}>Back Mockup</p>
//                           <img 
//                             src={design.images.back} 
//                             alt="Back Mockup" 
//                             style={{ width: '80px', height: 'auto', border: '1px solid #ddd' }} 
//                           />
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 )}
//               </div>

//               {/* Design Details */}
//               <div>
//                 <h4>Design Details</h4>
//                 <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
//                   <p><strong>Product:</strong> {design.productName || 'N/A'}</p>
//                   <p><strong>GSM:</strong> {design.productGSM || 'N/A'}</p>
//                   <p><strong>Description:</strong> {design.description || 'N/A'}</p>
                  
//                   <h5>Pricing</h5>
//                   <p><strong>Print Cost:</strong> ₹{design.PrintCost || 'N/A'}</p>
//                   <p><strong>Base Cost:</strong> ₹{design.baseCost || 'N/A'}</p>
//                   <p><strong>Total Price:</strong> ₹{design.totalPrice || 'N/A'}</p>
//                   <p><strong>Selling Price:</strong> ₹{design.sellingPrice || 'N/A'}</p>

//                   {design.costs && (
//                     <div>
//                       <h5>Print Costs Breakdown</h5>
//                       <p><strong>Front:</strong> ₹{design.costs.front || 'N/A'}</p>
//                       <p><strong>Back:</strong> ₹{design.costs.back || 'N/A'}</p>
//                     </div>
//                   )}

//                   {design.Printsizes && (
//                     <div>
//                       <h5>Print Sizes</h5>
//                       {design.Printsizes.front && (
//                         <p><strong>Front:</strong> {design.Printsizes.front.width} × {design.Printsizes.front.height}</p>
//                       )}
//                       {design.Printsizes.back && (
//                         <p><strong>Back:</strong> {design.Printsizes.back.width} × {design.Printsizes.back.height}</p>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>

//             {/* SKUs */}
//             <div style={{ marginTop: '15px' }}>
//               <h5>Available SKUs</h5>
//               <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
//                 {design.SKUs && design.SKUs.length > 0 ? (
//                   design.SKUs.map((sku, skuIndex) => (
//                     <span 
//                       key={skuIndex}
//                       style={{ 
//                         backgroundColor: '#e9ecef',
//                         padding: '4px 8px',
//                         borderRadius: '4px',
//                         fontSize: '12px',
//                         border: '1px solid #dee2e6'
//                       }}
//                     >
//                       {sku}
//                     </span>
//                   ))
//                 ) : (
//                   <span style={{ color: '#6c757d', fontSize: '12px' }}>No SKUs available</span>
//                 )}
//               </div>
//             </div>

//             {/* Timestamp */}
//             {design.timestamp && (
//               <div style={{ marginTop: '15px', fontSize: '12px', color: '#6c757d' }}>
//                 <strong>Created:</strong> {new Date(design.timestamp.seconds * 1000).toLocaleString()}
//               </div>
//             )}
//           </div>
//         ))}
//       </div>

//       {designs.length === 0 && !loading && (
//         <p style={{ textAlign: 'center', margin: '40px', fontSize: '18px', color: '#6c757d' }}>
//           No designs found for this brand.
//         </p>
//       )}
//     </div>
//   );
// };

// export default BrandDesignDetails;